/**
 * /well-servicing/rates — the Well Servicing rate card (GET /well-servicing/rates,
 * fieldiq-api#1294, WS-4).
 *
 * Full width. Filters in the URL: the card (`card=default`, a customer's id,
 * or every card) and the kind. A rate reads in the company's currency format —
 * `CA$650.00/hr`; a third-party rate has no rate of its own and reads as
 * `At cost` or `Cost + 15%` — and an absent figure is `—`. New and edit are full pages
 * (RateDetailScreen), never a dialog.
 */
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Tags } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { useAuth } from '../../../contexts/AuthContext';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { useWsCustomers, useWsMeta } from '../../../hooks/useWellServicing';
import { useWsRates } from '../../../hooks/useWsTickets';
import { queryLoadFailure } from '../../../lib/load-failure';
import { canWriteWsTickets } from '../../../lib/ws-tickets';
import { catalogLabel, wsDay } from '../../../lib/well-servicing';
import type { WsRate, WsTicketCatalog } from '../../../types/ws-tickets';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useOpenRecord } from '../../downhole/recordPage';
import { WS_EYEBROW, WS_INPUT, WsChip, WsChipRow, WsEmpty, WsPanel, WsRegister, type WsColumn } from '../WsBits';
import { wsRatePriceText } from './TicketBits';

/** What a rate prices beyond its kind: the crew position, the unit or the equipment type, how a third party is billed. */
export function wsRateAppliesTo(r: WsRate, crewPositions?: { value: string; label: string }[]) {
  if (r.crewPosition) return r.crewPositionLabel || catalogLabel(crewPositions, r.crewPosition);
  if (r.equipmentId) return r.equipmentUnitNumber ? `${r.equipmentUnitNumber} · ${r.equipmentName ?? '—'}` : r.equipmentName || '—';
  if (r.equipmentTypeId) return r.equipmentTypeName || '—';
  if (r.kind === 'third_party') return r.pricingLabel || catalogLabel(undefined, r.pricing);
  return '—';
}

export default function WsRatesScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const canWrite = canWriteWsTickets(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const card = searchParams.get('card') ?? '';
  const kind = searchParams.get('kind') ?? '';
  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };
  const catalog = (useWsMeta().data?.data ?? null) as WsTicketCatalog | null;
  const customers = useWsCustomers().data ?? [];
  const listQuery = useWsRates({ customer_id: card || undefined, kind: kind || undefined });
  const allQuery = useWsRates();
  const rates = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (r: WsRate) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const today = allQuery.data?.meta.today ?? null;
  const live = (r: WsRate) => !!today && r.effectiveFrom <= today && (!r.effectiveTo || r.effectiveTo >= today);
  const filtered = !!(card || kind);

  const columns: WsColumn<WsRate>[] = [
    { key: 'code', header: 'Code', render: (r) => (<><p className="font-mono font-extrabold text-white">{r.code || '—'}</p><p className="mt-0.5 text-xs text-fieldiq-muted">{r.name || '—'}</p></>) },
    { key: 'card', header: 'Card', render: (r) => (r.customerId ? r.customerName || '—' : 'Default card') },
    { key: 'kind', header: 'Kind', render: (r) => r.kindLabel || catalogLabel(catalog?.priceKinds, r.kind) },
    { key: 'applies', header: 'Applies to', render: (r) => wsRateAppliesTo(r, catalog?.crewPositions) },
    { key: 'unit', header: 'Unit', render: (r) => r.unitLabel || catalogLabel(catalog?.priceUnits, r.unit) },
    { key: 'rate', header: 'Rate', align: 'right', render: (r) => <span className="font-mono">{wsRatePriceText(r)}</span> },
    { key: 'currency', header: 'Currency', render: (r) => <span className="font-mono">{r.currency || '—'}</span> },
    { key: 'period', header: 'Effective', render: (r) => `${wsDay(r.effectiveFrom)} – ${r.effectiveTo ? wsDay(r.effectiveTo) : 'no end'}` },
  ];

  return (
    <div data-testid="ws-rates-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WS_EYEBROW}
        title="Rate Card"
        description="What the company bills — rig time, standby, travel, crew, equipment, rent-ins, fuel, third-party and other charges — on the default card or a customer's own."
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <Tags className="size-4" aria-hidden="true" />, label: 'Rates', tone: 'neutral', loading: allQuery.isLoading, value: countOf(() => true) },
          { icon: <Tags className="size-4" aria-hidden="true" />, label: 'In effect today', tone: 'accent', loading: allQuery.isLoading, value: countOf(live) },
          { icon: <Tags className="size-4" aria-hidden="true" />, label: 'Customer rates', tone: 'neutral', loading: allQuery.isLoading, value: countOf((r) => !!r.customerId) },
        ]}
        actions={
          canWrite ? (
            <button
              type="button"
              className={DH_BAND_SECONDARY}
              onClick={() => navigate(`/well-servicing/rates/new${card && card !== 'default' ? `?customerId=${card}` : ''}`)}
            >
              <Plus className="size-4" aria-hidden="true" />
              New rate
            </button>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WsChipRow label="Filter by kind">
            <WsChip label="All kinds" active={!kind} onClick={() => setParam('kind', '')} />
            {(catalog?.priceKinds ?? []).map((k) => (
              <WsChip key={k.value} label={k.label} active={kind === k.value} onClick={() => setParam('kind', k.value)} count={allQuery.isLoading ? undefined : countOf((r) => r.kind === k.value)} />
            ))}
          </WsChipRow>
          <div className="grid gap-3 sm:grid-cols-[16rem]">
            <select className={WS_INPUT} aria-label="Filter by card" value={card} onChange={(e) => setParam('card', e.target.value)}>
              <option value="">Every card</option>
              <option value="default">Default card</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <WsPanel eyebrow="Rate card" title="Rates" testId="ws-rate-register" footer={!listQuery.isLoading && rates.length ? `${rates.length} rate${rates.length === 1 ? '' : 's'}` : undefined}>
            <WsRegister
              query={listQuery}
              rows={rates}
              columns={columns}
              rail={(r) => (live(r) ? 'border-l-fi-green' : 'border-l-fi-steel')}
              onOpen={(r) => openRecord(`/well-servicing/rates/${r.id}`)}
              subject="rates"
              testId="ws-rate"
              minWidth="min-w-[1080px]"
              empty={filtered ? <WsEmpty>No rate matches these filters.</WsEmpty> : <WsEmpty>No rates yet. Add the first one to price field tickets.</WsEmpty>}
              card={(r) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-extrabold text-white">{r.code || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">{r.name || '—'} · {r.customerId ? r.customerName || '—' : 'Default card'}</p>
                    </div>
                    <span className="font-mono text-sm text-white">{wsRatePriceText(r)}</span>
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {r.kindLabel || catalogLabel(catalog?.priceKinds, r.kind)} · {wsRateAppliesTo(r, catalog?.crewPositions)} · from {wsDay(r.effectiveFrom)}
                  </p>
                </>
              )}
            />
          </WsPanel>
        </div>
      </section>
    </div>
  );
}
